import { getSafeAlertParams, getSafeBannerString } from '@/components/Banner/AlertBannerUtils';
import DisplayHtml from '@/components/common/DisplayHtml';
import type { Banner } from '@/models/types';
import { useUserHasRoles } from '@/shared/auth/useUserAffectations';
import DirectusIcon from '@/shared/icons/DirectusIcon';
import { Alert } from '@mantine/core';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';

type Props = {
  item: Banner;
};

const AlertBanner = ({ item }: Props) => {
  const bannerRoles = item.roles ?? [];

  const rolesToCheck = bannerRoles.reduce((acc: number[], bannerRole) => {
    if (isObject(bannerRole.Role_id)) {
      const role = bannerRole.Role_id;
      acc.push(role.id);
    } else if (isNumber(bannerRole.Role_id)) {
      acc.push(bannerRole.Role_id);
    }
    return acc;
  }, []);

  const { data } = useUserHasRoles(rolesToCheck);

  if (item.status !== 'published' || !data) {
    return null;
  }

  const startDate = item.startDate ? new Date(item.startDate) : null;
  const endDate = item.endDate ? new Date(item.endDate) : null;
  const now = new Date();

  if (startDate && startDate > now) {
    return null; // Start date is in the future, do not display the banner
  }

  if (endDate && endDate < now) {
    return null; // End date is in the past, do not display the banner
  }

  const safeAlertParams = getSafeAlertParams(item.params);
  const safeBannerColor = getSafeBannerString(item.color);
  const safeBannerIcon = getSafeBannerString(item.icon);
  const alertTitle = item.name ?? safeAlertParams.title;
  const alertColor = safeBannerColor ?? safeAlertParams.color;
  const alertIcon = safeBannerIcon ? <DirectusIcon icon={safeBannerIcon} size="24" /> : undefined;

  return (
    <Alert {...safeAlertParams} title={alertTitle} color={alertColor} icon={alertIcon}>
      <DisplayHtml html={item.content} allowTargetBlank />
    </Alert>
  );
};

export default AlertBanner;
